// 4-week booking calendar shared by /book (pilot) and /admin/:slug (admin).
//
// Visual language (per Harry's brief — minimal color):
//   • plain cell   → free / no signal needed
//   • amber dot    → others have bookings here (pilot mode)
//   • count badge  → admin sees the day's booking count (admin mode)
//   • accent ring  → pilot has bookings here (pilot mode) /
//                    admin's currently-selected day (admin mode)
//   • red fill     → admin closed the day
//   • muted        → past day or fully booked
//
// Caller wires up data and click behavior; this component is pure render.

import {
  buildCalendarWeeks, calendarMonthLabel, todayInMskYmd,
} from "../lib/calendar.js";

const RU_WD = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const RU_WD_LONG = [
  "понедельник","вторник","среда","четверг","пятница","суббота","воскресенье",
];
const RU_MONTHS_SHORT = [
  "янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек",
];

export default function BookingCalendar({
  daysByDate = {},
  todayYmd: todayProp,
  mode = "pilot",
  selectedYmd = null,
  weeks: weekCount = 4,
  onPick,
}) {
  const today = todayProp || todayInMskYmd();
  const weeks = buildCalendarWeeks(today, weekCount);
  const monthLabel = calendarMonthLabel(weeks);
  return (
    <div className="rcf-cal">
      <div className="rcf-cal__month">{monthLabel}</div>
      <div className="rcf-cal__weekhdr" aria-hidden="true">
        {RU_WD.map((w) => <span key={w} className="rcf-cal__wd">{w}</span>)}
      </div>
      <div className="rcf-cal__grid">
        {weeks.flat().map((ymd) => (
          <CalendarCell
            key={ymd}
            ymd={ymd}
            today={today}
            day={daysByDate[ymd]}
            mode={mode}
            selected={ymd === selectedYmd}
            onPick={onPick}
          />
        ))}
      </div>
      <Legend mode={mode} />
    </div>
  );
}

function CalendarCell({ ymd, today, day, mode, selected, onPick }) {
  const isPast = ymd < today;
  const isToday = ymd === today;
  const closed = day?.airspace_status === "closed";
  const anyAvailable = !!day?.slots?.some((s) => s.anyAvailable);
  const totalBookings = day?.bookings_count || 0;
  const mine = day?.my_bookings_count || 0;

  // State machine differs per role.
  //   pilot: gated on availability — past/full/closed are non-bookable.
  //   admin: every day is clickable; the cell still surfaces "closed" and
  //          past-vs-future muting to keep visual parity with the pilot view.
  let state;
  let clickable;
  if (mode === "admin") {
    state = closed ? "closed" : isPast ? "past" : "free";
    clickable = true;
  } else {
    if (closed) { state = "closed"; clickable = false; }
    else if (isPast || !day) { state = "past"; clickable = false; }
    else if (!anyAvailable) { state = "full"; clickable = false; }
    else { state = "free"; clickable = true; }
  }

  const cls = [
    "rcf-cal__day",
    `rcf-cal__day--${state}`,
    isToday ? "rcf-cal__day--today" : "",
    selected ? "rcf-cal__day--selected" : "",
    mode === "pilot" && mine > 0 ? "rcf-cal__day--mine" : "",
  ].filter(Boolean).join(" ");

  const [, , dd] = ymd.split("-");
  return (
    <button
      type="button"
      className={cls}
      aria-disabled={!clickable}
      aria-label={ariaLabel({ ymd, mode, closed, anyAvailable, isPast, mine, totalBookings })}
      onClick={clickable ? () => onPick?.(ymd) : undefined}
    >
      <span className="rcf-cal__dd">{Number(dd)}</span>
      {mode === "pilot" && mine > 0 && (
        <span className="rcf-cal__mine" aria-hidden="true">{mine}</span>
      )}
      {mode === "admin" && totalBookings > 0 && (
        <span className="rcf-cal__count" aria-hidden="true">{totalBookings}</span>
      )}
      {mode === "pilot" && !closed && state !== "past" && totalBookings > mine && (
        <span className="rcf-cal__dot" aria-hidden="true" />
      )}
    </button>
  );
}

function Legend({ mode }) {
  if (mode === "admin") {
    return (
      <div className="rcf-cal__legend">
        <span><i className="rcf-cal__sw rcf-cal__sw--free" /> свободно</span>
        <span><i className="rcf-cal__sw rcf-cal__sw--count" /> есть записи</span>
        <span><i className="rcf-cal__sw rcf-cal__sw--closed" /> закрыто</span>
        <span><i className="rcf-cal__sw rcf-cal__sw--selected" /> выбран</span>
      </div>
    );
  }
  return (
    <div className="rcf-cal__legend">
      <span><i className="rcf-cal__sw rcf-cal__sw--free" /> свободно</span>
      <span><i className="rcf-cal__sw rcf-cal__sw--others" /> есть записи</span>
      <span><i className="rcf-cal__sw rcf-cal__sw--full" /> мест нет</span>
      <span><i className="rcf-cal__sw rcf-cal__sw--closed" /> закрыто</span>
      <span><i className="rcf-cal__sw rcf-cal__sw--mine" /> ваша запись</span>
    </div>
  );
}

function ariaLabel({ ymd, mode, closed, anyAvailable, isPast, mine, totalBookings }) {
  const [y, m, d] = ymd.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  const dow = (utc.getUTCDay() + 6) % 7;
  const base = `${RU_WD_LONG[dow]} ${d} ${RU_MONTHS_SHORT[m - 1]}`;
  const parts = [base];
  if (closed) parts.push("поле закрыто");
  else if (isPast) parts.push("прошедший день");
  else if (mode === "pilot" && !anyAvailable) parts.push("мест нет");
  else if (mode === "pilot") parts.push("свободно");
  if (mode === "admin" && totalBookings > 0) parts.push(`${totalBookings} ${plural(totalBookings, "запись","записи","записей")}`);
  if (mode === "pilot" && mine > 0) parts.push(`у вас ${mine} ${plural(mine, "запись","записи","записей")}`);
  return parts.join(", ");
}

function plural(n, one, few, many) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
